'use client';

import React, { useEffect, useMemo, useRef, useState } from 'react';

import { useRouter } from 'next/navigation';

import useExitApp from '@/hooks/useExitApp';
import { usePlatformState } from '@/hooks/usePlatformState';

import { useGlobalConfig } from '@/components/GlobalContext';

import { useApp } from '@/app/frontdoor/AppContext';
import { frontdoorBackgroundUrl } from '@/app/frontdoor/backgroundImage';
import { useConfig } from '@/appconfig/ConfigContext';
import { CraveErrorCode } from '@/enums';
import {
  SignOutReason,
  log,
  setCommonMonitoringAttributes,
} from '@/services/appMonitoringSuite';
import { StoredAccountResponse, getAccessToken, getStoredAccount } from '@/services/auth';
import { useAuth } from '@/services/auth/authContext';
import { useProductIntro, useUserExperiences } from '@/services/graphQL';
import { oneTrustGetBannerUI } from '@/services/userEngagementSuite/onetrust';
import { getSystem } from '@/system';
import { Profile } from '@/types';
import { deeplinkStorage } from '@/utils/deeplinkStorage';
import { dispatchFatalError } from '@/utils/fatalError';
import { preloadImage } from '@/utils/imagePreload';
import { consumeInactiveReturnRoute } from '@/utils/inactiveReturnRoute';
import { buildImageUrl } from '@/utils/mediaHelpers';
import { shouldShowProductIntro } from '@/utils/productIntro';

import SplashLoader from './SplashLoader';

const SplashScreen: React.FC = () => {
  const config = useConfig();
  const { userLanguage, userMaturity, currentResolution, setUserExperienceInformation } =
    useGlobalConfig();
  const { get: getUserXDetails, loading, error, data } = useUserExperiences();

  const router = useRouter();
  const {
    isAuthenticated,
    setIsAuthenticated,
    setIsInitializing,
    getProfiles,
    refreshToken,
    logout,
    applySession,
  } = useAuth();

  const [profiles, setProfiles] = useState<Profile[]>();
  const isTokenStale = useRef<boolean>(false);

  const { app, homeNavId, error: appQueryError, refetch } = useApp();

  const { productIntro, loading: productIntroLoading } = useProductIntro({
    userLanguage,
    userMaturity,
  });

  const showProductIntro = useMemo(() => {
    return (
      config?.featureFlags?.productIntro && shouldShowProductIntro(app, productIntro)
    );
  }, [config, app, productIntro]);

  // Warms the intro's first frame while the splash is still up. Must match the
  // URL app/product-intro builds, or the navigation refetches.
  useEffect(() => {
    if (!showProductIntro) return;
    preloadImage(
      buildImageUrl(
        productIntro?.screens[0]?.image.url,
        { height: 1080 },
        currentResolution
      )
    );
  }, [showProductIntro, productIntro, currentResolution]);

  // A signed-out user reaches the frontdoor with or without the intro. Warm it only
  // once the intro decision is settled, so it never competes with the intro's image.
  useEffect(() => {
    if (isAuthenticated || !config || productIntroLoading || showProductIntro) return;
    preloadImage(frontdoorBackgroundUrl(app, currentResolution));
  }, [
    isAuthenticated,
    config,
    productIntroLoading,
    showProductIntro,
    app,
    currentResolution,
  ]);

  const system = getSystem();

  const { ExitModal } = useExitApp({
    userLanguage,
    enableBackListener: true,
  });

  const effectiveIsAppInactive = usePlatformState();

  useEffect(() => {
    if (effectiveIsAppInactive) return;
    if (!loading && data && profiles && app) {
      setUserExperienceInformation(data);

      if (homeNavId) {
        handleProfileRedirect(profiles);
      }
    } else if (error) {
      logout(SignOutReason.USERACCOUNTINFORMATIONFAILED).then(goToFrontDoor);
    }
  }, [loading, data, profiles, homeNavId, app, error, effectiveIsAppInactive]);

  useEffect(() => {
    if (appQueryError) {
      const onError = async () => {
        if (isTokenStale.current) {
          log('Splashscreen-App: Token is stale, logging out user', 'debug');
          await logout(SignOutReason.TOKENMISSING);
          refetch();
          return;
        }

        isTokenStale.current = true;
        log('Splashscreen-App Query Fetch Error', 'info');

        // A failed refresh has already signed the user out or raised the error overlay;
        // either way there is no session left to refetch for.
        if (!(await refreshToken())) {
          log('Splashscreen-App: token refresh failed, app query not refetched', 'error');
          return;
        }

        refetch();
      };

      onError();
    }
  }, [appQueryError]);

  const [splashDone, setSplashDone] = useState(false);

  useEffect(() => {
    if (!config) return;
    if (effectiveIsAppInactive) return;

    if (app && splashDone) {
      isTokenStale.current = false;
      onLoad();
    }
  }, [app, config, splashDone, effectiveIsAppInactive]);
  const onLoad = async () => {
    const accessToken = await getAccessToken();

    // False only once the session is gone: the sign-out redirect or the error overlay is
    // already in flight, so the first-run pushes below would race it for no benefit. The
    // deep link still runs, since /deep-link routes a signed-out user to sign in and back.
    let sessionAlive = true;

    if (accessToken) {
      sessionAlive = await checkUserProfiles();
    } else {
      setIsInitializing(false);
    }

    if (sessionAlive && config?.featureFlags?.oneTrust) {
      const bannerUI = await oneTrustGetBannerUI(userLanguage);
      if (bannerUI?.appConfig?.showBanner) {
        router.push('/onetrust');
        return;
      }
    }

    if (sessionAlive && showProductIntro) {
      router.push('/product-intro');
      return;
    }

    const deepLinkId = system?.getDeepLinkId?.();
    if (deepLinkId) {
      deeplinkStorage.setProcessing();
      router.push(`/deep-link?contentId=${deepLinkId}`);
      return;
    }

    if (!accessToken) {
      if (!deeplinkStorage.isProcessing()) {
        goToFrontDoor();
      }
    }
  };

  // Reports whether the session survived, which is what onLoad gates its first-run
  // routing on. Every false path has already signed the user out or raised the overlay.
  const checkUserProfiles = async (): Promise<boolean> => {
    try {
      const userProfiles = await getProfiles();

      if (userProfiles && userProfiles.length === 0)
        throw new Error('Profile call failed');

      setIsAuthenticated(true);
      setProfiles(userProfiles);
      getUserXDetails();
      return true;
    } catch (e) {
      try {
        // A failed refresh has already signed the user out or raised the error overlay
        if (!(await refreshToken())) {
          log('Splashscreen-Profile Check: token refresh failed', 'error');
          return false;
        }

        const userProfiles = await getProfiles();

        if (userProfiles && userProfiles.length === 0)
          throw new Error('Profile call failed');

        setProfiles(userProfiles);
        getUserXDetails();
        return true;
      } catch (e) {
        log('Splashscreen-Profile Check failed, logging out user', 'error');
        handleLogout();
        return false;
      }
    } finally {
      setIsInitializing(false);
    }
  };

  const handleLogout = async () => {
    const storedAccount: StoredAccountResponse | null = getStoredAccount();
    if (storedAccount && storedAccount.bdu) {
      log('SplashScreen-HandleLogout: Loging out with an expired BDU session', 'debug');
      await logout(SignOutReason.BDUSESSIONEXPIRED);
    } else if (storedAccount) {
      log('SplashScreen-HandleLogout: Loging out with an expired DTC session', 'debug');
      await logout(SignOutReason.DTCSESSIONEXPIRED);
    } else {
      log('SplashScreen-HandleLogout: Loging out with a missing token session', 'debug');
      await logout(SignOutReason.TOKENMISSING);
    }

    // prevent the redirect if the user is entering via a deep link
    if (!deeplinkStorage.isProcessing()) {
      goToFrontDoor();
      return;
    }

    // Suppressing the redirect leaves the user on the splash with no session and nothing
    // reported, so the overlay stands in for it.
    log('SplashScreen-HandleLogout: redirect suppressed by deep link', 'error');
    dispatchFatalError(CraveErrorCode.CONNECTION_ERROR_4003);
  };

  const handleProfileRedirect = async (profiles: Profile[]) => {
    if (deeplinkStorage.isProcessing()) return;

    const inactiveReturnRoute = consumeInactiveReturnRoute();
    if (inactiveReturnRoute) {
      return router.replace(inactiveReturnRoute);
    }

    // multiple profiles
    if (profiles.length > 1) return router.replace('/profile');

    // single profile
    if (profiles.length === 1) {
      const [{ id, hasPin }] = profiles;

      await applySession(profiles[0]);
      await setCommonMonitoringAttributes(profiles[0]);

      if (hasPin) return router.replace(`/profile/enter-pin?id=${id}`);
    }

    // no profiles OR single profile without pin
    router.push(`/screen?id=${homeNavId || ''}`);
  };

  const goToFrontDoor = () => {
    router.replace('/frontdoor');
  };

  // if the app is fully loaded and ready to redirect but a deep-link
  // processing flag is still blocking handleProfileRedirect, a real deep-link
  // flow would have navigated to /deep-link (unmounting this screen) by now.
  // If still here, clear it and retry so the splash can never hang indefinitely
  useEffect(() => {
    if (effectiveIsAppInactive) return;
    if (loading || !data || !profiles || !app || !homeNavId) return;
    if (!deeplinkStorage.isProcessing()) return;

    const timer = setTimeout(() => {
      // A genuine deep-link flow lives on /deep-link and would have unmounted
      // if still on splash, the flag is stale.
      if (window.location.pathname === '/deep-link') return;
      if (deeplinkStorage.isProcessing()) {
        log(
          'SplashScreen: deep-link processing watchdog fired — clearing stale flag',
          'error'
        );
        deeplinkStorage.clearProcessing();
      }
      handleProfileRedirect(profiles);
    }, 10000);

    return () => clearTimeout(timer);
  }, [loading, data, profiles, app, homeNavId, effectiveIsAppInactive]);

  return (
    <>
      <SplashLoader
        onDone={() => setSplashDone(true)}
        isInactive={effectiveIsAppInactive}
      />
      {ExitModal}
    </>
  );
};

export default SplashScreen;
